import React, { useState, useEffect } from 'react';
import { landlordApi } from '../../services/api';
import Navbar from '../Dashboard/components/Navbar';

interface Affiliate {
    id: string;
    email: string;
    businessName?: string;
    status: string;
    referralCount?: number;
    commissionRate?: number;
    totalEarnings?: number;
    createdAt?: string;
}

export default function AffiliatesAdmin() {
    const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dashboardData, setDashboardData] = useState<any>(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken') || localStorage.getItem('idToken');

            const headers = { Authorization: `Bearer ${token}` };

            // Fetch dashboard analytics
            const dashRes = await landlordApi.get('api/affiliates/dashboard', { headers });
            setDashboardData(dashRes.data?.data);

            // Fetch affiliates list
            const affiliatesRes = await landlordApi.get('api/affiliates/referrals', { headers });
            setAffiliates(affiliatesRes.data?.data || []);
        } catch (err: any) {
            console.error('Failed to fetch affiliates:', err);
            setError(err.response?.data?.error?.message || 'Failed to load affiliates');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <i className="fas fa-spinner fa-spin text-4xl text-blue-600 mb-4"></i>
                    <p className="text-gray-600">Loading Affiliates Dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Navbar />
            <div className="max-w-7xl mx-auto p-8 w-full flex-1">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-gray-900">Affiliates Admin Dashboard</h1>
                    <p className="text-gray-600 mt-2">Manage affiliate program, referrals, and commissions</p>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-800">{error}</p>
                    </div>
                )}

                {/* Analytics Cards */}
                {dashboardData && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="text-sm text-gray-600 mb-1">Total Affiliates</div>
                            <div className="text-3xl font-bold text-gray-900">
                                {dashboardData.totalAffiliates || 0}
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="text-sm text-gray-600 mb-1">Active Referrals</div>
                            <div className="text-3xl font-bold text-blue-600">
                                {dashboardData.activeReferrals || 0}
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="text-sm text-gray-600 mb-1">Total Commissions Paid</div>
                            <div className="text-3xl font-bold text-green-600">
                                £{(dashboardData.totalCommissions || 0).toLocaleString()}
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="text-sm text-gray-600 mb-1">Pending Payouts</div>
                            <div className="text-3xl font-bold text-orange-600">
                                £{(dashboardData.pendingPayouts || 0).toLocaleString()}
                            </div>
                        </div>
                    </div>
                )}

                {/* Affiliates Table */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-xl font-bold text-gray-900">Affiliate List</h2>
                    </div>

                    {affiliates.length === 0 ? (
                        <div className="p-6 text-center text-gray-500">
                            <i className="fas fa-inbox text-4xl mb-4 opacity-30"></i>
                            <p>No affiliates found</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-700">Email</th>
                                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-700">Business</th>
                                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-700">Referrals</th>
                                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-700">Commission Rate</th>
                                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-700">Total Earnings</th>
                                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-700">Status</th>
                                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-700">Joined</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {affiliates.map(aff => (
                                        <tr key={aff.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 text-sm text-gray-900">{aff.email}</td>
                                            <td className="px-6 py-4 text-sm text-gray-600">{aff.businessName || '—'}</td>
                                            <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                                                {aff.referralCount || 0}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900">
                                                {(aff.commissionRate || 0) * 100}%
                                            </td>
                                            <td className="px-6 py-4 text-sm text-green-600 font-medium">
                                                £{(aff.totalEarnings || 0).toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 text-sm">
                                                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                                    aff.status === 'active' ? 'bg-green-100 text-green-800' :
                                                    aff.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                                                    'bg-yellow-100 text-yellow-800'
                                                }`}>
                                                    {aff.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600">
                                                {aff.createdAt ? new Date(aff.createdAt).toLocaleDateString() : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
